import useLocalDataStore from "@/hooks/common/useLocalDataStore";
import useOverlay, {OverlayState} from "@/hooks/common/useOverlay";
import SharePopup from "@COMPONENTS/common/modal/share/Mobile";
import * as style from "@COMPONENTS/style/productlist/departurepopupM";
import {PopupContainer} from "@FRAMEWORK/ui/mobile/popup";
import useEventByGoodsMonth from "@QUERIES/prdt/event/useEventByMonth";
import useGoodsDetail from "@QUERIES/prdt/goods/useGoodsDetail";
import useGoodsAvailEvent from "@QUERIES/prdt/goods/useGoodsOneAvailEvent";
import useGoodsOption from "@QUERIES/prdt/goods/useGoodsOption";
import dayjs, {Dayjs} from "dayjs";
import {useRouter} from "next/router";
import {useEffect, useMemo, useRef, useState} from "react";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import useFilterStore from "@COMPONENTS/product/pages/localList/common/filterHook";
import {D_DELETE_EXCLUDE_KEYS, D_EXCLUDE_KEYS} from "@COMPONENTS/product/common/popup/common/constant";
import {EventExt, FilterType} from "@COMPONENTS/product/common/popup/desktop/EventListPopup/EventListPopup";
import EventListInfo from "@COMPONENTS/product/common/popup/mobile/EventListPopup/EventListInfo";
import EventListFilters from "@COMPONENTS/product/common/popup/mobile/EventListPopup/EventlistFilters";
import SelectSchedule from "@COMPONENTS/product/common/popup/mobile/EventListPopup/SelectSchedule";
import OptionPopup from "@COMPONENTS/product/common/popup/mobile/EventListPopup/popup/OptionPopup";
import TrafficPopup from "@COMPONENTS/product/common/popup/mobile/EventListPopup/popup/TrafficPopup";
import {appBottomScheme} from "@COMPONENTS/common/commonUtil";
import {WithCalendarRef} from "@COMPONENTS/hoc/withCalendar";
import {Area} from "@FRAMEWORK/ui/mobile/layout";
import {SubTitle} from "@FRAMEWORK/ui/mobile/text";
import EventListCalendar from "@COMPONENTS/product/common/popup/mobile/EventListPopup/EventListCalendar";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";

interface Props {
	goodsCd: string;
	startMonth?: string;
}

const EventListPopup = ({goodsCd, startMonth = "", ...overlayProps}: OverlayState & Props) => {
	const {data: availEventData} = useGoodsAvailEvent(goodsCd);
	const availableEvent = availEventData?.body;
	// 상품상세 출발일 변경 클릭 시 초기 Month 설정
	if (availableEvent && startMonth) {
		availableEvent.evStartDt = dayjs(startMonth).format("YYYYMMDD");
	}

	const router = useRouter();
	const dspSid = router.query?.dspSid as string;
	const {getFilter} = useFilterStore();
	const optionFetch = useGoodsOption(goodsCd, {staleTime: 1000 * 60});
	const option = optionFetch.data?.body;
	const productDetailFetch = useGoodsDetail(goodsCd, {staleTime: 1000 * 60});
	const productDetail = productDetailFetch.data?.body;

	// 전체 필터 관리
	const {getLocalData, setLocalData, removeLocalData} = useLocalDataStore();
	const filters = (getLocalData("departureFilters") as FilterType[]) || [];

	// 전체 필터 초기값 설정
	useEffect(() => {
		if (!option) return;
		// localList filter
		const currentFilter = getFilter()?.filter(v => v.key !== "sort") || [];
		// 예약 가능 옵션 활성화 (localList filter 중복 제거)
		let result = [...currentFilter.filter(v => v.key !== "airCd")];
		// 출발일정 설정
		const outStartDateList = currentFilter.find(v => v.key === "outStartDateList");
		if (outStartDateList) {
			if (outStartDateList.value.length === 6) {
				result.push({key: "outStartMonth", text: outStartDateList.text, value: outStartDateList.value});
			} else if (outStartDateList.value.length === 8) {
				result.push({key: "outStartDt", text: outStartDateList.text, value: outStartDateList.value});
			}
			result = result.filter(v => v.key !== "outStartDateList");
		}

		// 상품 옵션 설정
		const {accommodationList, trafficList} = option;
		// 숙소 옵션 추가
		if (accommodationList?.length > 0 && productDetail?.webDisplayCls === "B") {
			const accommodation = accommodationList.map(item => ({
				key: "accomSeq",
				value: item.accomSeq.toString(),
				text: item.accomNm,
			}));
			result.push(...accommodation);
		}

		// 교통편 옵션 추가
		if (currentFilter.find(v => v.key === "airCd")) {
			// 필터에 선택된 항공사
			const airCdArray = currentFilter.filter(v => v.key === "airCd")?.map(i => i.value);
			const traffic = trafficList
				.filter(item => airCdArray.includes(item.trCompanySnm))
				.map(item => ({key: "trCompanySnm", value: item.trCompanySnm, text: item.trCompanyNm}));
			result.push(...traffic);
		} else {
			// 필터에 선택된 항공사가 없을 경우, 전부 추가
			const traffic = trafficList.map(item => ({
				key: "trCompanySnm",
				value: item.trCompanySnm,
				text: item.trCompanyNm,
			}));
			result.push(...traffic);
		}
		setLocalData({key: "departureFilters", value: result});

		return () => removeLocalData("departureFilters");
	}, [option]);

	// 추가 필터(교통편, 옵션)
	const [open] = useOverlay();

	const removeAllFilters = () => {
		const setArray = filters.filter(v => D_DELETE_EXCLUDE_KEYS.includes(v.key));
		setLocalData({key: "departureFilters", value: setArray});
	};

	const removeFilter = (key: string, value: string) => {
		const filtered = filters.filter(e => {
			if (key === "price") {
				return !(e.key === "minPrice" || e.key === "maxPrice");
			} else {
				return !(e.key === key && e.value === value);
			}
		});
		setLocalData({key: "departureFilters", value: filtered});
	};

	// 교통 팝업 열기
	const openTrafficPopup = async () => {
		PopupUtil.promise(TrafficPopup, {goodsCd, trafficList: option.trafficList}).then(() => appBottomScheme(false));
	};

	// 옵션 팝업 열기
	const openOptionPopup = async () => {
		PopupUtil.promise(OptionPopup, {goodsCd, optionList: option.accommodationList}).then(() => appBottomScheme(false));
	};

	// 출발일 선택(달력)
	const calendarRef = useRef<WithCalendarRef>(null);
	/** 행사리스트 필터링에 사용 (YYYYMMDD) */
	const [currentDate, setCurrentDate] = useState(null);
	/** 달력 세팅에 사용 (YYYYMM) */
	const [currentMonth, setCurrentMonth] = useState(null);

	/** 최소 날짜, 최대 날짜, 이벤트 날짜 세팅 */
	const [minDate, maxDate] = useMemo(() => {
		if (!availableEvent) return [null, null];
		const {minOutStartDt, maxOutStartDt} = availableEvent.eventMonthList;

		setCurrentMonth(dayjs(availableEvent.evStartDt).format("YYYYMM"));
		return [minOutStartDt, maxOutStartDt];
	}, [availableEvent]);

	/** 필터링 된 날짜 세팅 */
	useEffect(() => {
		if (!filters.length) return;
		const dateFilter = filters.find(v => v.key === "outStartDt");
		const monthFilter = filters.find(v => v.key === "outStartMonth");

		const newDate = dateFilter ? dateFilter.value : null;
		const newMonth = monthFilter ? monthFilter.value : null;

		newMonth && setCurrentMonth(newMonth);
		newDate && setCurrentMonth(dayjs(newDate).format("YYYYMM"));
		newDate && setCurrentDate(newDate);
	}, [filters, minDate]);

	/** 달력 동기화 */
	useEffect(() => {
		calendarRef.current?.monthChange(currentMonth);
	}, [currentMonth]);

	const onMonthChange = (date: Dayjs) => {
		const newMonth = date?.format("YYYYMM") || null;
		setCurrentMonth(newMonth);
	};

	const onDateChange = (date: Dayjs) => {
		const newDate = date?.format("YYYYMMDD") || null;
		setCurrentDate(newDate);
	};

	const eventByGoodsMonthFetch = useEventByGoodsMonth(dspSid, goodsCd, currentMonth, {
		enabled: !!currentMonth && !optionFetch.isLoading,
		staleTime: 1000 * 60,
		select(data: {body: EventExt[]}) {
			const eventData = data.body || [];
			// 필드 연산
			eventData.map(event => {
				const {evProgDivi, avlilBlockQty, adtPrice, airTaxAdtPrice, bafAdtPrice} = event;
				event.sumPrice = adtPrice + airTaxAdtPrice + bafAdtPrice;
				event.isEvReserve = "false"; // 예약가능 여부
				event.isEvConfirm = "false"; // 출발확정 여부

				if (
					evProgDivi !== "05" &&
					((evProgDivi !== "00" && avlilBlockQty > 0) || evProgDivi === "00" || avlilBlockQty <= 0)
				) {
					event.isEvReserve = "true";
				}
				if (evProgDivi === "07" || evProgDivi === "11") {
					event.isEvConfirm = "true";
				}
				if (evProgDivi === "05") {
					event.evReserveStatus = "end";
				} else if (evProgDivi === "00" || avlilBlockQty <= 0) {
					event.evReserveStatus = "wait";
				} else {
					event.evReserveStatus = "can";
				}
				return event.sumPrice;
			});
			return {minPrice: productDetail.minPrice, list: eventData};
		},
	});
	const organizedEventList = eventByGoodsMonthFetch.data;

	// 달력에 표기 되는 여행 리스트
	const eventsInCalendar = useMemo(() => {
		if (!organizedEventList) return;
		const {minPrice, list} = organizedEventList as {minPrice: number; list: Array<EventExt>};
		let copyArray = list;
		filters?.forEach(item => {
			const filterKey = item.key;
			if (D_EXCLUDE_KEYS.indexOf(filterKey) > -1) return;
			copyArray = copyArray.reduce((acc, event) => {
				const filterKeyList = filters.filter(v => v.key === filterKey);
				filterKeyList.some(filter => {
					const {key, value} = filter;
					if (key === "minPrice") return Number(event.sumPrice) >= Number(value);
					if (key === "maxPrice") return Number(event.sumPrice) <= Number(value);
					return event[key] && event[key].indexOf(value) > -1;
					// eslint-disable-next-line no-param-reassign
				}) && (acc = acc.concat(event));
				return acc;
			}, []);
		});

		return {minPrice, eventList: copyArray};
	}, [filters, organizedEventList, currentMonth]);

	// 공유하기
	const menu = router.query?.menu as string;
	const pathDspSid = `${router.query?.dspSid?.slice(0, -2)}00` as string;
	const handleShare = () => {
		open(overlay => (
			<SharePopup
				{...overlay}
				baseUrl={`${publicRuntimeConfig.ENV_PRODUCT_URL}/product/localList.m`}
				parameter={`menu=${menu}&dspSid=${pathDspSid}&goodsCd=${goodsCd}`}
				afterClose={() => appBottomScheme(false)}
			/>
		));
	};

	if (productDetailFetch.isFetching || productDetailFetch.isError || !eventsInCalendar || !availEventData) {
		return <></>;
	}

	return (
		<PopupContainer {...overlayProps} title="출발일 보기" size="full" animate="slideIn" padding={false}>
			<style.ContentWrap>
				{/* 상품 정보 및 배너 */}
				<EventListInfo goodsCd={goodsCd} productDetail={productDetail} handleShare={handleShare} />
				{/* 필터 */}
				<EventListFilters
					goodsCd={productDetail.goodsCd}
					removeAllFilters={removeAllFilters}
					removeFilter={removeFilter}
					option={option}
					productDetail={productDetail}
					openTrafficPopup={openTrafficPopup}
					openOptionPopup={openOptionPopup}
				/>
				{/* 출발일 선택 */}
				<ErrorBoundary logMsg="DeparetureDayPopup SelectDepartureDate Error">
					{currentMonth && (
						<Area>
							<style.TitleWrap>
								<SubTitle>출발일 선택</SubTitle>
								<style.ReservationBox>
									<style.ReservationText state="possible">예약가능</style.ReservationText>
									<style.ReservationText state="waiting">예약대기</style.ReservationText>
									<style.ReservationText state="deadline">예약마감</style.ReservationText>
								</style.ReservationBox>
							</style.TitleWrap>
							<EventListCalendar
								value={currentMonth}
								data={eventsInCalendar}
								minDate={minDate}
								maxDate={maxDate}
								onDateChange={onDateChange}
								onMonthChange={onMonthChange}
								ref={calendarRef}
							/>
						</Area>
					)}
				</ErrorBoundary>
				{/* 일정 선택 & 행사 리스트 */}
				<SelectSchedule eventsInCalendar={eventsInCalendar} currentDate={currentDate} />
			</style.ContentWrap>
		</PopupContainer>
	);
};

export default EventListPopup;
